/**
 * Store de sesión — login, logout, switch_location.
 *
 * Persiste a localStorage para que la sesión sobreviva a reloads. La key es
 * `riga-traza-session-v1`. Si en algún momento cambia el shape de la sesión,
 * bumpear la versión para forzar logout limpio.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Usuario } from '@/api/contracts/usuarios';
import type { Ubicacion } from '@/api/contracts/ubicaciones';
import { authApi } from '@/api/endpoints';
import { setSessionExpiredHandler } from '@/api/http';
import { puedeOperarEnUbicacion } from '@/domain/rbac';

interface SessionState {
  usuario: Usuario | null;
  loginAt: string | null;
  /** GLN actualmente activo — puede diferir del default si el usuario hizo switch. */
  ubicacionActivaId: string | null;
  loading: boolean;
  error: string | null;

  login: (username: string, password: string) => Promise<boolean>;
  /** Login con Google Workspace SSO (paquete AB). Recibe el idToken del flujo GIS. */
  loginConGoogle: (idToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
  /** Reset local de la sesión cuando el backend rechaza el token (token expirado
   *  o inválido). No llama al backend — el token ya no sirve. Lo dispara
   *  `apiFetch` automáticamente via setSessionExpiredHandler. */
  expireSession: () => void;
  switchUbicacion: (ubicacionId: string, ubicaciones: readonly Ubicacion[]) => boolean;
  /** Updatea `usuario.ubicacionDefaultId` localmente tras un PUT exitoso al
   *  backend. Solo refleja el estado — la mutation la dispara el componente. */
  setUbicacionDefault: (ubicacionId: string) => void;
}

/** GLN activo al iniciar sesión: el default del backend, o el primer operable. */
function pickUbicacionInicial(usuario: Usuario): string | null {
  return usuario.ubicacionDefaultId ?? usuario.ubicacionId ?? usuario.ubicacionesIds?.[0] ?? null;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      usuario: null,
      loginAt: null,
      ubicacionActivaId: null,
      loading: false,
      error: null,

      login: async (username, password) => {
        set({ loading: true, error: null });
        try {
          const { usuario, loginAt } = await authApi.login({ username, password });
          set({
            usuario,
            loginAt,
            ubicacionActivaId: pickUbicacionInicial(usuario),
            loading: false,
            error: null,
          });
          return true;
        } catch (err) {
          set({
            loading: false,
            error: err instanceof Error ? err.message : 'Error desconocido al iniciar sesión',
          });
          return false;
        }
      },

      loginConGoogle: async (idToken) => {
        set({ loading: true, error: null });
        try {
          const { usuario, loginAt } = await authApi.loginConGoogle({ idToken });
          set({
            usuario,
            loginAt,
            ubicacionActivaId: pickUbicacionInicial(usuario),
            loading: false,
            error: null,
          });
          return true;
        } catch (err) {
          set({
            loading: false,
            error: err instanceof Error ? err.message : 'Error en Google SSO',
          });
          return false;
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch {
          // Mock no falla; igual ignoramos errores acá para garantizar logout local
        }
        set({ usuario: null, loginAt: null, ubicacionActivaId: null, error: null });
      },

      expireSession: () => {
        // No llamamos al backend — el token ya no sirve. Solo reseteo local +
        // mensaje en el banner del login.
        set({
          usuario: null,
          loginAt: null,
          ubicacionActivaId: null,
          loading: false,
          error: 'Tu sesión expiró. Volvé a ingresar.',
        });
      },

      switchUbicacion: (ubicacionId, ubicaciones) => {
        const { usuario } = get();
        if (!usuario) return false;
        // Defensa: el usuario solo puede operar en GLNs permitidos
        if (!puedeOperarEnUbicacion(usuario, ubicacionId, ubicaciones)) return false;
        set({ ubicacionActivaId: ubicacionId });
        return true;
      },

      setUbicacionDefault: (ubicacionId) => {
        const { usuario } = get();
        if (!usuario) return;
        set({ usuario: { ...usuario, ubicacionDefaultId: ubicacionId } });
      },
    }),
    {
      name: 'riga-traza-session-v1',
      storage: createJSONStorage(() => localStorage),
      // Solo persistir lo necesario — error y loading son transientes
      partialize: (state) => ({
        usuario: state.usuario,
        loginAt: state.loginAt,
        ubicacionActivaId: state.ubicacionActivaId,
      }),
      // Migración silenciosa al rehidratar: sesiones viejas (pre-refactor del
      // login, cuando `useUbicaciones()` corría con 401 antes del token) pueden
      // tener `ubicacionActivaId = null` aunque el usuario tenga GLN asignado.
      // Lo recomputamos desde el propio usuario para que el dashboard no quede
      // mostrando "—".
      onRehydrateStorage: () => (state, error) => {
        if (error || !state) return;
        if (state.usuario && !state.ubicacionActivaId) {
          state.ubicacionActivaId = pickUbicacionInicial(state.usuario);
        }
      },
    },
  ),
);

/** Hook conveniente — true si hay sesión activa. */
export function useIsAuthenticated(): boolean {
  return useSessionStore((s) => s.usuario !== null);
}

// Cuando `apiFetch` detecta token expirado/inválido, dispara este handler.
// Lo registramos una sola vez al inicializarse el módulo del store.
setSessionExpiredHandler(() => {
  useSessionStore.getState().expireSession();
});
